/**
 * Shared dashboard primitives (industrial dark theme).
 * Styling lives in src/styles/dashboardTheme.css ("dsh-" namespace).
 *
 * All components are presentational — data + handlers are passed in from MainForm.
 */
import React from 'react';
import { FiChevronDown, FiChevronRight, FiArrowUp, FiArrowDown, FiMinus } from 'react-icons/fi';
import '../../styles/dashboardTheme.css';

/* ------------------------------------------------------------------ */
/* Tone helpers                                                        */
/* ------------------------------------------------------------------ */
export const TONES = {
  primary:   { color: '#38bdf8', soft: 'rgba(56,189,248,0.14)' },
  blue:      { color: '#60a5fa', soft: 'rgba(59,130,246,0.16)' },
  success:   { color: '#22c55e', soft: 'rgba(34,197,94,0.14)' },
  warning:   { color: '#f59e0b', soft: 'rgba(245,158,11,0.16)' },
  danger:    { color: '#ef4444', soft: 'rgba(239,68,68,0.16)' },
  secondary: { color: '#a78bfa', soft: 'rgba(167,139,250,0.16)' },
  amber:     { color: '#fbbf24', soft: 'rgba(251,191,36,0.14)' },
  teal:      { color: '#2dd4bf', soft: 'rgba(45,212,191,0.14)' },
  muted:     { color: '#94a3b8', soft: 'rgba(148,163,184,0.14)' },
};
export const tone = (name) => TONES[name] || TONES.muted;

/** Utilization % → semantic tone (green good, amber ok, red bad). */
export const utilizationTone = (pct) => {
  if (pct == null) return 'muted';
  if (pct >= 75) return 'success';
  if (pct >= 45) return 'warning';
  return 'danger';
};

/** Machine live state → tone + label. */
export const machineStateMeta = (state) => {
  switch (state) {
    case 'RUNNING': return { tone: 'success', label: 'Kjører', pulse: true };
    case 'IDLE': return { tone: 'warning', label: 'Venter' };
    case 'ALARM': return { tone: 'danger', label: 'Alarm' };
    case 'EMERGENCY': return { tone: 'danger', label: 'Nødstopp' };
    case 'OFFLINE': return { tone: 'muted', label: 'Frakoblet' };
    default: return state ? { tone: 'muted', label: state } : { tone: 'muted', label: 'Ukjent' };
  }
};

/** Order display status → tone + label. */
export const orderStatusMeta = (status) => {
  switch (status) {
    case 'in_progress': return { tone: 'primary', label: 'I produksjon' };
    case 'planned': return { tone: 'muted', label: 'Planlagt' };
    case 'waiting': return { tone: 'warning', label: 'Venter materiale' };
    case 'done': return { tone: 'success', label: 'Ferdig' };
    case 'stopped': return { tone: 'danger', label: 'Stoppet' };
    default: return { tone: 'muted', label: status || '—' };
  }
};

/* ------------------------------------------------------------------ */
/* Section header                                                       */
/* ------------------------------------------------------------------ */
export const SectionHeader = ({ icon: Icon, title, meta, right, className = '' }) => (
  <div className={`dsh-section__head ${className}`}>
    {Icon && <span className="dsh-section__icon"><Icon size={14} /></span>}
    <h3 className="dsh-section__title">{title}</h3>
    {meta != null && <span className="dsh-section__meta">{meta}</span>}
    {right && <div className="dsh-section__right">{right}</div>}
  </div>
);

/* ------------------------------------------------------------------ */
/* Status badge                                                         */
/* ------------------------------------------------------------------ */
export const StatusBadge = ({ tone: t = 'muted', children, dot = true, pulse = false, title, style }) => (
  <span className={`dsh-badge dsh-badge--${t} ${pulse ? 'dsh-badge--pulse' : ''}`} title={title} style={style}>
    {dot && <span className="dsh-badge__dot" />}
    {children}
  </span>
);

/* ------------------------------------------------------------------ */
/* Progress bar                                                         */
/* ------------------------------------------------------------------ */
export const ProgressBar = ({ value = 0, tone: t = 'primary', color, thin = false, title }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className={`dsh-progress ${thin ? 'dsh-progress--thin' : ''}`} title={title}>
      <div className="dsh-progress__fill" style={{ width: `${pct}%`, background: color || tone(t).color }} />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* KPI card                                                             */
/* ------------------------------------------------------------------ */
/**
 * delta: { value: number, label?: string, invert?: boolean } — positive = up arrow (green by default).
 * subtext: string rendered in place of delta when no delta is available.
 */
export const KpiCard = ({
  icon: Icon, label, value, delta, subtext, subtone, tone: t = 'primary',
  onClick, open = false, compact = false, children, title, style, textColor, bgColor, accent = true,
}) => {
  const tn = tone(t);
  const Comp = onClick ? 'button' : 'div';
  let deltaEl = null;
  if (delta && delta.value != null && !Number.isNaN(delta.value)) {
    const v = Number(delta.value);
    const good = delta.invert ? v <= 0 : v >= 0;
    const cls = v === 0 ? '' : good ? 'is-up' : 'is-down';
    const Arrow = v > 0 ? FiArrowUp : v < 0 ? FiArrowDown : FiMinus;
    deltaEl = (
      <span className={`dsh-kpi__delta ${cls}`}>
        <Arrow size={11} />
        {v > 0 ? `+${v}` : v}{delta.label ? ` ${delta.label}` : ''}
      </span>
    );
  } else if (subtext) {
    deltaEl = <span className={`dsh-kpi__delta ${subtone ? `is-${subtone}` : ''}`}>{subtext}</span>;
  }
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={title}
      className={`dsh-card dsh-kpi ${compact ? 'dsh-kpi--compact' : ''} ${onClick ? 'dsh-card--click' : ''}`}
      style={{ ...(bgColor ? { background: bgColor } : {}), ...style }}
    >
      {accent && <span className="dsh-card__accent" style={{ background: tn.color }} />}
      {Icon && (
        <span className="dsh-kpi__icon" style={{ background: tn.soft, color: tn.color }}>
          <Icon size={compact ? 14 : 17} strokeWidth={2} />
        </span>
      )}
      <span className="dsh-kpi__body">
        <span className="dsh-kpi__label" style={textColor ? { color: textColor } : undefined}>{label}</span>
        <span className={`dsh-kpi__value ${compact ? 'dsh-kpi__value--sm' : ''}`} style={textColor ? { color: textColor } : undefined}>{value}</span>
        {deltaEl}
      </span>
      {children && <span className="dsh-kpi__extra">{children}</span>}
      {onClick && <FiChevronDown size={14} className={`dsh-kpi__chev ${open ? 'is-open' : ''}`} />}
    </Comp>
  );
};

/* ------------------------------------------------------------------ */
/* Machine card                                                         */
/* ------------------------------------------------------------------ */
export const MachineCard = ({ name, type, hours, unit = 'h', utilization, state, sub, onClick, title }) => {
  const st = machineStateMeta(state);
  const utilT = state === 'ALARM' || state === 'EMERGENCY' ? 'danger' : utilizationTone(utilization);
  const c = tone(utilT).color;
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp type={onClick ? 'button' : undefined} onClick={onClick} title={title} className={`dsh-card dsh-machine ${onClick ? 'dsh-card--click' : ''}`}>
      <div className="dsh-machine__head">
        <div style={{ minWidth: 0 }}>
          <div className="dsh-machine__name">{name}</div>
          {type && <div className="dsh-machine__type">{type}</div>}
        </div>
        <StatusBadge tone={st.tone} pulse={st.pulse}>{st.label}</StatusBadge>
      </div>
      <div className="dsh-machine__row">
        <span className="dsh-machine__hours dsh-mono" style={{ color: 'var(--dsh-text)' }}>
          {hours != null ? `${new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 1 }).format(hours)} ${unit}` : '—'}
        </span>
        <span className="dsh-machine__pct dsh-mono" style={{ color: c }}>{utilization != null ? `${Math.round(utilization)}%` : '—'}</span>
      </div>
      <ProgressBar value={utilization || 0} color={c} />
      {sub && <div className="dsh-machine__foot">{sub}</div>}
    </Comp>
  );
};

/* ------------------------------------------------------------------ */
/* Application card                                                     */
/* ------------------------------------------------------------------ */
export const AppCard = ({ icon, emoji, title, description, tone: t = 'primary', onClick, disabled, badge, badgeColor, textColor, bgColor }) => {
  const tn = tone(t);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="dsh-card dsh-card--click dsh-app"
      title={description}
      style={bgColor ? { background: bgColor } : undefined}
    >
      {badge > 0 && <span className="dsh-app__badge" style={{ background: badgeColor || tn.color }}>{badge > 99 ? '99+' : badge}</span>}
      <span className="dsh-app__icon" style={{ background: tn.soft, color: tn.color }}>
        {icon ? React.createElement(icon, { size: 26 }) : <span aria-hidden>{emoji}</span>}
      </span>
      <span className="dsh-app__body">
        <p className="dsh-app__title" style={textColor ? { color: textColor } : undefined}>{title}</p>
        {description && <p className="dsh-app__desc" style={textColor ? { color: textColor, opacity: 0.75 } : undefined}>{description}</p>}
      </span>
      <FiChevronRight size={15} className="dsh-app__arrow" />
    </button>
  );
};

/* ------------------------------------------------------------------ */
/* Alert item (right sidebar)                                           */
/* ------------------------------------------------------------------ */
export const AlertItem = ({ icon: Icon, message, sub, time, severity = 'info', onClick, open, expandable = false }) => {
  const t = severity === 'danger' ? 'danger' : severity === 'warning' ? 'warning' : severity === 'success' ? 'success' : 'primary';
  const tn = tone(t);
  const cls = severity === 'danger' ? 'dsh-alert--danger' : severity === 'warning' ? 'dsh-alert--warning' : '';
  return (
    <button type="button" onClick={onClick} className={`dsh-alert ${cls}`}>
      <span className="dsh-alert__icon" style={{ background: tn.soft, color: tn.color }}>{Icon && <Icon size={14} />}</span>
      <span className="dsh-alert__body">
        <p className="dsh-alert__msg">{message}</p>
        {sub && <p className="dsh-alert__sub">{sub}</p>}
      </span>
      {time && <span className="dsh-alert__time">{time}</span>}
      {expandable && <FiChevronDown size={13} style={{ color: 'var(--dsh-text-faint)', flexShrink: 0, marginTop: 4, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
    </button>
  );
};

/* ------------------------------------------------------------------ */
/* Quick filter chip                                                    */
/* ------------------------------------------------------------------ */
export const QuickFilter = ({ label, tone: t = 'muted', count, onClick, title }) => (
  <button type="button" className="dsh-filter" onClick={onClick} title={title}>
    <span className="dsh-filter__dot" style={{ background: tone(t).color }} />
    {label}
    {count != null && <span className="dsh-mono" style={{ color: 'var(--dsh-text-faint)', fontSize: 10.5 }}>{count}</span>}
  </button>
);

/* ------------------------------------------------------------------ */
/* Order row (today's orders)                                           */
/* ------------------------------------------------------------------ */
export const OrderRow = ({ orderNumber, title, meta, quantity, status, progress, onClick }) => {
  const st = orderStatusMeta(status);
  const barTone = status === 'stopped' ? 'danger' : status === 'done' ? 'success' : status === 'planned' ? 'muted' : 'primary';
  const inProgress = status === 'in_progress';
  return (
    <button type="button" className={`dsh-order ${inProgress ? 'dsh-order--active' : ''}`} onClick={onClick}>
      <span className="dsh-order__num">
        {inProgress && <span className="dsh-order__live" title="I produksjon" />}
        {orderNumber}
      </span>
      <span className="dsh-order__desc">
        <span className="dsh-order__title">{title || '—'}</span>
        {meta && <span className="dsh-order__meta">{meta}</span>}
      </span>
      <span className="dsh-order__qty">{quantity != null ? `${quantity} stk` : '—'}</span>
      <span><StatusBadge tone={st.tone} pulse={inProgress}>{st.label}</StatusBadge></span>
      <span className="dsh-order__prog">
        <ProgressBar value={progress} tone={barTone} thin />
        <span className="dsh-order__pct">{Math.round(progress || 0)}%</span>
      </span>
    </button>
  );
};

export const OrderListHeader = () => (
  <div className="dsh-orders__head">
    <span>Ordrenr</span><span>Beskrivelse</span><span>Antall</span><span>Status</span><span>Fremdrift</span>
  </div>
);

/* ------------------------------------------------------------------ */
/* Donut chart (pure SVG, no deps)                                      */
/* ------------------------------------------------------------------ */
export const DonutChart = ({ segments = [], size = 120, stroke = 12, centerValue, centerLabel }) => {
  const total = segments.reduce((s, x) => s + (Number(x.value) || 0), 0);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="dsh-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0, transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(148,163,184,0.14)" strokeWidth={stroke} />
        {total > 0 && segments.map((s, i) => {
          const len = (Number(s.value) || 0) / total * c;
          const el = (
            <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={tone(s.tone).color}
              strokeWidth={stroke} strokeDasharray={`${Math.max(0, len - 1.5)} ${c}`} strokeDashoffset={-offset} strokeLinecap="butt" />
          );
          offset += len;
          return el;
        })}
        <g transform={`rotate(90 ${size / 2} ${size / 2})`}>
          <text x="50%" y="50%" dy="-0.05em" textAnchor="middle" fontSize={size * 0.21} fontWeight="700" fill="#e5e9f0" fontFamily="inherit">{centerValue}</text>
          {centerLabel && <text x="50%" y="50%" dy="1.45em" textAnchor="middle" fontSize={size * 0.085} fill="#94a3b8" fontFamily="inherit">{centerLabel}</text>}
        </g>
      </svg>
      <div className="dsh-donut__legend">
        {segments.map((s, i) => (
          <div key={i} className="dsh-donut__row">
            <span className="dsh-donut__dot" style={{ background: tone(s.tone).color }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
            <b>{s.value}</b>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Left navigation                                                      */
/* ------------------------------------------------------------------ */
/**
 * items: [{ id, label, icon, onClick, active, count }]
 * groups: [{ label, items }] rendered below the primary items.
 */
export const LeftNav = ({ collapsed, onToggle, items = [], groups = [], footer }) => (
  <nav className={`dsh-nav ${collapsed ? 'is-collapsed' : ''}`} aria-label="Hovednavigasjon">
    <div className="dsh-nav__top">
      <button type="button" className="dsh-iconbtn" onClick={onToggle} title={collapsed ? 'Utvid meny' : 'Skjul meny'} style={{ height: 30, minWidth: 30 }}>
        <FiChevronRight size={15} style={{ transform: collapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
      </button>
    </div>
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
      <div className="dsh-nav__list">
        {items.map((it) => (
          <button key={it.id} type="button" onClick={it.onClick} title={it.label} className={`dsh-nav__item ${it.active ? 'is-active' : ''}`}>
            <span className="dsh-nav__icon">{it.icon && React.createElement(it.icon, { size: 17 })}</span>
            <span className="dsh-nav__text">{it.label}</span>
            {it.count > 0 && <span className="dsh-nav__count">{it.count}</span>}
          </button>
        ))}
      </div>
      {groups.map((g) => (
        <div key={g.label}>
          <div className="dsh-nav__label">{g.label}</div>
          <div className="dsh-nav__list" style={{ paddingTop: 0 }}>
            {g.items.map((it) => (
              <button key={it.id} type="button" onClick={it.onClick} title={it.label} className={`dsh-nav__item ${it.active ? 'is-active' : ''}`} style={{ height: 34, fontSize: 12.5, fontWeight: 500 }}>
                <span className="dsh-nav__icon">{it.icon && React.createElement(it.icon, { size: 15 })}</span>
                <span className="dsh-nav__text">{it.label}</span>
                {it.count > 0 && <span className="dsh-nav__count">{it.count}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
    {footer && <div className="dsh-nav__foot">{footer}</div>}
  </nav>
);
